import * as React from 'react';
import _ from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import {
  CLEAR_EXPERIMENT_LIST,
  IApplicationState,
  ProjectPickerActions,
} from '@store';

import { ProjectPickerHelper } from '@utils';
import { IAccountProject } from '@models';
import { useToggle } from '@hooks';

import { ReactComponent as ImmunoSEQLogo } from '@assets/images/immunoSEQ.svg';
import { ReactComponent as ClonoSEQLogo } from '@assets/images/clonoSEQ.svg';

import './ProjectPicker.scss';

enum UIFlags {
  'UNKNOWN' = 'UNKOWN',
  'DROPDOWN_DISABLED' = 'DROPDOWN_DISABLED',
  'DROPDOWN_ENABLED_OPEN' = 'DROPDOWN_ENABLED_OPEN',
  'DROPDOWN_ENABLED_CLOSED' = 'DROPDOWN_ENABLED_CLOSED',
}

export const ProjectPicker: React.FC = () => {
  // Redux - connects component to store, enabling it to dispatch to ActionCreators.
  const dispatch = useDispatch();
  // get the inital methods to setup the ProjectPicker for different user types.
  // UI Flag - summarizes the different states for the ProjectPicker UI
  const [uiFlag, setUIFlag] = React.useState<UIFlags>(UIFlags.UNKNOWN);
  // Dropdown Toggle - manages the state of the dropdown
  const [dropdownStatus, toggle] = useToggle();

  const [activeProject, setActiveProject] =
    React.useState<IAccountProject | null>(null);

  // Display Format - this holds the final display format that the UI will recieve
  const [displayFormat, setDisplayFormat] =
    React.useState<IAccountProject[][] | null>(null);

  // Project Pickers' - @Store --> ProjectPicker --> Project
  const activeProjectId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeProjectId
  );
  // Project Pickers' - @Store --> ProjectPicker --> Project
  const projectsList = useSelector(
    (state: IApplicationState) => state.projectPicker.projectsList
  );

  // IF - the projectList gets updated
  React.useEffect(() => {
    if (activeProjectId && projectsList) {
      const activeProject = projectsList.filter(
        ({ projectId }) => projectId === activeProjectId
      )[0];

      setActiveProject(activeProject);

      // loop through the projectsList and filter on brand
      const sortedProjects =
        ProjectPickerHelper.sortProjectsByBrand(projectsList);
      setDisplayFormat(sortedProjects);
    }
  }, [activeProjectId, projectsList]);
  const getBrandLogo = (project: IAccountProject) => {
    return project.brand === 'ImmunoSEQ' ? (
      <ImmunoSEQLogo className='immuno-seq' />
    ) : (
      <ClonoSEQLogo className='clono-seq' />
    );
  };

  // UI - styling flags
  React.useEffect(() => {
    if (displayFormat && _.flatten(displayFormat).length > 1) {
      // dropdown status: enabled
      if (dropdownStatus) {
        // dropdown: open
        setUIFlag(UIFlags.DROPDOWN_ENABLED_OPEN);
      } else {
        // dropdown; closed
        setUIFlag(UIFlags.DROPDOWN_ENABLED_CLOSED);
      }
    } else {
      // dropdown status: disabled
      setUIFlag(UIFlags.DROPDOWN_DISABLED);
    }
  }, [dropdownStatus, displayFormat]);

  const handleSelection = (project: IAccountProject) => {
    dispatch({ type: CLEAR_EXPERIMENT_LIST });
    dispatch(ProjectPickerActions.updateProjectPicker(project));
    toggle();
  };

  const updateIcon = () => {
    const className =
      uiFlag === UIFlags.DROPDOWN_ENABLED_OPEN
        ? 'fas fa-caret-down'
        : uiFlag === UIFlags.DROPDOWN_ENABLED_CLOSED
        ? 'fas fa-caret-down'
        : '';

    // NOTE: the version of fontAwesome we're currently using doesn't register
    // react state changes, to force React to detect the change we need to
    // generate a new "random key" everytime so when the component re-renders the
    // change gets picked up. (we need to update to a newer version v5+ will fix this)
    return (
      <div id='drop-icon' key={Math.floor(Math.random() * 100)}>
        <i className={`${className}`} />
      </div>
    );
  };

  return (
    <>
      {activeProject && displayFormat !== null ? (
        <div id='project-picker'>
          <div
            id='overlay'
            className={
              uiFlag === UIFlags.DROPDOWN_ENABLED_OPEN ? 'show-overlay' : ''
            }
            onClick={() => toggle()}
          ></div>

          <div
            id='active-project'
            className={
              uiFlag === UIFlags.DROPDOWN_DISABLED
                ? 'disable'
                : uiFlag === UIFlags.DROPDOWN_ENABLED_OPEN
                ? 'active-project-open'
                : ''
            }
            onClick={() =>
              uiFlag !== UIFlags.DROPDOWN_DISABLED ? toggle() : null
            }
          >
            <div id='active-project-brand'>{activeProject.projectName}</div>
            <div id='divider'>|</div>
            <div id='active-project-logo' className='logo'>
              {getBrandLogo(activeProject)}
            </div>
            {updateIcon()}
          </div>
          <div
            id='project-picker-dropdown-wrapper'
            className={uiFlag !== UIFlags.DROPDOWN_ENABLED_OPEN ? 'hide' : ''}
          >
            <div id='project-picker-dropdown'>
              {displayFormat.map((brand, i) => {
                return (
                  <div className='brand' key={i}>
                    {brand.map((project, p) => {
                      return (
                        <div
                          className='project'
                          key={p}
                          onClick={() => handleSelection(project)}
                        >
                          {project.projectName}
                        </div>
                      );
                    })}
                    <div className='logo'>{getBrandLogo(brand[0])}</div>
                    {i < displayFormat.length - 1 ? <hr /> : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
